/**
 * Profile Generator — constants, defaults and layer factories.
 * Pure JS (the app is JS + the em design system, not the doc's TS/Tailwind).
 * Literal hex values are used everywhere the 2D canvas draws.
 */

export const uid = () =>
  `l_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

// ── Canvas ───────────────────────────────────────────────────────────────────
export const DEFAULT_CANVAS = {
  width: 400,
  height: 500,
  zoom: 1,
  gridSize: 10,
  showGrid: true,
  showSafeMargin: true,
  safeMargin: 20,
  snapToGrid: true,
};

export const CANVAS_PRESETS = [
  { label: 'Portrait card (400×500)', width: 400, height: 500 },
  { label: 'Square (500×500)', width: 500, height: 500 },
  { label: 'Story (450×600)', width: 450, height: 600 },
  { label: 'Wide banner (640×360)', width: 640, height: 360 },
  { label: 'LinkedIn (480×480)', width: 480, height: 480 },
];

export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 3;

// ── Image adjustments ─────────────────────────────────────────────────────────
export const DEFAULT_ADJUSTMENTS = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  blur: 0,
  shadowEnabled: false,
  shadowColor: '#00000055',
  shadowBlur: 12,
  shadowOffsetX: 0,
  shadowOffsetY: 6,
  blendMode: 'source-over',
  flipH: false,
  flipV: false,
  // Crop fields kept for forward-compat (interactive crop tool is a later pass).
  cropEnabled: false,
  cropX: 0,
  cropY: 0,
  cropW: 1,
  cropH: 1,
};

// 16 canvas globalCompositeOperation values.
export const BLEND_MODES = [
  'source-over', 'multiply', 'screen', 'overlay', 'darken', 'lighten',
  'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference',
  'exclusion', 'hue', 'saturation', 'color', 'luminosity',
];

// ── Fonts ──────────────────────────────────────────────────────────────────────
export const FONT_FAMILIES = [
  "'Inter', sans-serif",
  'Arial, sans-serif',
  'Georgia, serif',
  "'Times New Roman', serif",
  "'Courier New', monospace",
  "'Trebuchet MS', sans-serif",
  'Verdana, sans-serif',
];

export const FONT_WEIGHTS = [
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semibold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'Extrabold' },
];

// ── Asset library ────────────────────────────────────────────────────────────
export const ASSET_CATEGORIES = [
  'Abstract', 'Geometric', 'Gradient', 'Pattern', 'Photo', 'Texture', 'Frame', 'Overlay', 'Other',
];

// ── Templates ──────────────────────────────────────────────────────────────────
export const TEMPLATE_CATEGORIES = [
  'Engineer Spotlight', 'Leadership Bio', 'Team Member', 'New Hire Welcome',
  'Creative Portfolio', 'Minimal', 'Custom',
];

// ── Layer factories ─────────────────────────────────────────────────────────────
const baseLayer = (type, name) => ({
  id: uid(),
  type,
  name,
  x: 0, y: 0,
  width: 100, height: 100,
  rotation: 0,
  opacity: 1,
  visible: true,
  locked: false,
});

export const makeBackgroundLayer = (cfg = DEFAULT_CANVAS) => ({
  ...baseLayer('background', 'Background'),
  x: 0, y: 0, width: cfg.width, height: cfg.height,
  fill: '#f4efe5',
  fillType: 'solid',
  gradientFrom: '#f4efe5',
  gradientTo: '#d8c9b0',
  gradientAngle: 0,
});

export const makeForegroundLayer = (cfg = DEFAULT_CANVAS) => ({
  ...baseLayer('foreground', 'Foreground'),
  x: 0, y: 0, width: cfg.width, height: cfg.height,
  fill: 'rgba(0,0,0,0.05)',
  fillType: 'solid',
  gradientFrom: 'rgba(0,0,0,0)',
  gradientTo: 'rgba(0,0,0,0.35)',
  gradientAngle: 0,
  opacity: 1,
});

export const makeImageLayer = (src, cfg = DEFAULT_CANVAS, extra = {}) => ({
  ...baseLayer('image', 'Image'),
  x: Math.round((cfg.width - 160) / 2),
  y: Math.round((cfg.height - 160) / 2),
  width: 160, height: 160,
  src,
  storagePath: extra.storagePath || null,
  adjustments: { ...DEFAULT_ADJUSTMENTS },
  ...extra,
});

// Box model: (x,y) = top-left of the text box; text is laid out inside
// [x..x+width] per textAlign and vertically centred. Keeps selection/resize
// uniform with the other layer types.
export const makeNameLayer = (cfg = DEFAULT_CANVAS) => ({
  ...baseLayer('name', 'Name'),
  x: 20,
  y: Math.round(cfg.height - 112),
  width: cfg.width - 40, height: 44,
  text: 'Full Name',
  fontSize: 24,
  fontFamily: "'Inter', sans-serif",
  fontColor: '#1a1a1a',
  fontWeight: '600',
  textAlign: 'center',
});

// A sensible starting scene for a brand-new profile.
export const makeStarterLayers = (cfg = DEFAULT_CANVAS) => [
  makeBackgroundLayer(cfg),
  makeNameLayer(cfg),
];

export const LAYER_TYPE_LABEL = {
  background: 'Background',
  image: 'Image',
  foreground: 'Foreground',
  name: 'Name',
};

export const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB
export const AUTOSAVE_INTERVAL = 30_000;
